import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import axios from "axios";

// Detecta URL da API (mesma lógica do api.js)
const isProd = process.env.NODE_ENV === "production";
const API_URL = isProd
  ? "https://api.assaicartola2.shop/api/"
  : "http://localhost:5137/api/";

const POLLING_INTERVAL_MS = 5000; // 5 segundos
const MAX_POLLING_SECONDS = 600;  // 10 minutos

// -------- Styled Components --------

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 32px 28px;
  max-width: 440px;
  width: 95%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
`;

const Titulo = styled.h2`
  font-size: 1.3rem;
  color: #222;
  margin-bottom: 8px;
`;

const Valor = styled.p`
  font-size: 1.5rem;
  font-weight: bold;
  color: #009245;
  margin-bottom: 16px;
`;

const QrImg = styled.img`
  width: 200px;
  height: 200px;
  margin: 0 auto 16px;
  display: block;
  border: 2px solid #eee;
  border-radius: 8px;
`;

const CopiaColaBox = styled.div`
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.75rem;
  word-break: break-all;
  color: #444;
  margin-bottom: 12px;
  text-align: left;
  max-height: 80px;
  overflow-y: auto;
`;

const BtnCopiar = styled.button`
  background: #009245;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 0.95rem;
  cursor: pointer;
  margin-bottom: 16px;
  width: 100%;
  &:hover { background: #007a38; }
`;

const BtnFechar = styled.button`
  background: transparent;
  color: #888;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 0.9rem;
  cursor: pointer;
  width: 100%;
  &:hover { background: #f5f5f5; }
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled.div`
  border: 3px solid #eee;
  border-top-color: #009245;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 0.8s linear infinite;
  margin: 20px auto;
`;

const StatusMsg = styled.p`
  color: ${(p) => (p.sucesso ? "#009245" : p.erro ? "#c0392b" : "#555")};
  font-weight: ${(p) => (p.sucesso || p.erro ? "bold" : "normal")};
  margin: 12px 0;
`;

const Temporizador = styled.p`
  font-size: 0.82rem;
  color: #aaa;
  margin-top: 4px;
`;

const ValoresGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 16px 0;
`;

const BtnValor = styled.button`
  background: ${(p) => (p.selecionado ? "#009245" : "#f5f5f5")};
  color: ${(p) => (p.selecionado ? "#fff" : "#333")};
  border: 2px solid ${(p) => (p.selecionado ? "#009245" : "#ddd")};
  border-radius: 8px;
  padding: 12px 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #009245; color: #009245; background: ${(p) => (p.selecionado ? "#007a38" : "#e8f5e9")}; }
`;

const InputValor = styled.input`
  width: 100%;
  border: 2px solid #ddd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 1rem;
  text-align: center;
  margin-top: 4px;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #009245; }
`;

const BtnConfirmar = styled.button`
  background: #009245;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  margin-top: 12px;
  &:hover { background: #007a38; }
  &:disabled { background: #ccc; cursor: not-allowed; }
`;

// -------- Componente --------

/**
 * Modal de PIX via Mercado Pago.
 * Props:
 *   - idUsuario (number): ID do usuário logado
 *   - valor (number): valor em R$ a cobrar
 *   - onClose (function): chamado ao fechar o modal
 *   - onPago (function): chamado quando pagamento confirmado
 */
const VALORES_PRESET = [5, 10, 20, 50, 100];

export default function ModalPix({ idUsuario, valor: valorInicial, onClose, onPago }) {
  const [etapa, setEtapa] = useState(valorInicial ? "carregando" : "seletor");
  const [valorSelecionado, setValorSelecionado] = useState(valorInicial || null);
  const [valorManual, setValorManual] = useState("");
  const [dados, setDados] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [segundosRestantes, setSegundosRestantes] = useState(MAX_POLLING_SECONDS);
  const [erroMsg, setErroMsg] = useState("");

  function confirmarValor() {
    const v = valorSelecionado || parseFloat(valorManual.replace(",", "."));
    if (!v || v <= 0) return;
    setValorSelecionado(v);
    setEtapa("carregando");
  }

  function selecionarPreset(v) {
    setValorSelecionado(v);
    setValorManual("");
  }

  function handleManualChange(e) {
    const raw = e.target.value.replace(/[^0-9,\.]/g, "");
    setValorManual(raw);
    setValorSelecionado(null);
  }

  const valorFinal = valorSelecionado || (valorManual ? parseFloat(valorManual.replace(",", ".")) : 0);

  // Cria cobrança quando entra na etapa "carregando"
  useEffect(() => {
    if (etapa !== "carregando") return;
    let cancelado = false;

    async function criarCobranca() {
      try {
        const res = await axios.post(`${API_URL}pix/criar`, {
          idUsuario,
          valor: valorFinal,
        });
        if (!cancelado) {
          setDados(res.data);
          setEtapa("aguardando");
        }
      } catch (err) {
        if (!cancelado) {
          setErroMsg("Não foi possível gerar a cobrança PIX. Tente novamente.");
          setEtapa("erro");
        }
      }
    }

    criarCobranca();
    return () => { cancelado = true; };
  }, [etapa]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling de status
  const consultarStatus = useCallback(async () => {
    if (!dados?.extReference) return;
    try {
      const res = await axios.get(`${API_URL}pix/status/${dados.extReference}`);
      const status = res.data.status;

      if (status === "APROVADO") {
        setEtapa("pago");
        if (onPago) onPago();
      } else if (status === "EXPIRADO" || status === "CANCELADO") {
        setEtapa("erro");
        setErroMsg(`Cobrança ${status.toLowerCase()}. Feche e tente novamente.`);
      }
    } catch {
      // Ignora erros de rede no polling — tentará de novo
    }
  }, [dados, onPago]);

  useEffect(() => {
    if (etapa !== "aguardando") return;

    const intervalo = setInterval(() => {
      consultarStatus();
      setSegundosRestantes((s) => {
        if (s <= 1) {
          clearInterval(intervalo);
          setEtapa("erro");
          setErroMsg("Tempo expirado. Feche e tente novamente.");
          return 0;
        }
        return s - POLLING_INTERVAL_MS / 1000;
      });
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(intervalo);
  }, [etapa, consultarStatus]);

  function copiarCodigo() {
    if (!dados?.qrCode) return;
    navigator.clipboard.writeText(dados.qrCode).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 3000);
    });
  }

  const minutosRestantes = Math.ceil(segundosRestantes / 60);

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal>
        <Titulo>Pagar via PIX</Titulo>

        {etapa === "seletor" && (
          <>
            <p style={{ color: "#666", marginBottom: 4 }}>Escolha o valor do depósito:</p>
            <ValoresGrid>
              {VALORES_PRESET.map((v) => (
                <BtnValor
                  key={v}
                  selecionado={valorSelecionado === v}
                  onClick={() => selecionarPreset(v)}
                >
                  R$ {v}
                </BtnValor>
              ))}
            </ValoresGrid>
            <p style={{ fontSize: "0.85rem", color: "#888", margin: "8px 0 4px" }}>Ou digite outro valor:</p>
            <InputValor
              type="text"
              inputMode="decimal"
              placeholder="Ex: 35,00"
              value={valorManual}
              onChange={handleManualChange}
            />
            <BtnConfirmar
              onClick={confirmarValor}
              disabled={valorFinal <= 0}
            >
              Gerar QR Code — R$ {valorFinal > 0 ? valorFinal.toFixed(2).replace(".", ",") : "0,00"}
            </BtnConfirmar>
            <BtnFechar style={{ marginTop: 8 }} onClick={onClose}>Cancelar</BtnFechar>
          </>
        )}

        {etapa === "carregando" && (
          <>
            <p style={{ color: "#666", marginBottom: 8 }}>Gerando cobrança...</p>
            <Spinner />
          </>
        )}

        {etapa === "aguardando" && dados && (
          <>
            <Valor>R$ {Number(dados.valor ?? valorFinal).toFixed(2).replace(".", ",")}</Valor>

            {dados.qrCodeBase64 && (
              <QrImg
                src={`data:image/png;base64,${dados.qrCodeBase64}`}
                alt="QR Code PIX"
              />
            )}

            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: 8 }}>
              Ou use o <strong>Pix Copia e Cola</strong>:
            </p>

            <CopiaColaBox>{dados.qrCode}</CopiaColaBox>

            <BtnCopiar onClick={copiarCodigo}>
              {copiado ? "✓ Copiado!" : "Copiar Código PIX"}
            </BtnCopiar>

            <StatusMsg>Aguardando confirmação do pagamento...</StatusMsg>
            <Temporizador>Expira em ~{minutosRestantes} min</Temporizador>
            <Spinner />
          </>
        )}

        {etapa === "pago" && (
          <>
            <p style={{ fontSize: "3rem", margin: "16px 0" }}>✅</p>
            <StatusMsg sucesso>Pagamento confirmado!</StatusMsg>
            <p style={{ color: "#555", marginBottom: 16 }}>
              Seu saldo foi atualizado com sucesso.
            </p>
            <BtnFechar onClick={onClose}>Fechar</BtnFechar>
          </>
        )}

        {etapa === "erro" && (
          <>
            <p style={{ fontSize: "3rem", margin: "16px 0" }}>❌</p>
            <StatusMsg erro>{erroMsg}</StatusMsg>
            <BtnFechar onClick={onClose}>Fechar</BtnFechar>
          </>
        )}

        {etapa !== "pago" && etapa !== "erro" && (
          <BtnFechar
            style={{ marginTop: 12 }}
            onClick={onClose}
          >
            Cancelar
          </BtnFechar>
        )}
      </Modal>
    </Overlay>
  );
}
